import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';

import {
  fetchClaims,
  updateClaim as updateClaimService,
  type FetchClaimsParams,
  type FetchClaimsResponse,
  type UpdateClaimPayload,
} from '@/services/claims';

export function useClaims(params?: FetchClaimsParams) {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery<FetchClaimsResponse, Error>({
    queryKey: ['claims', params?.user, params?.limit, params?.exclusiveStartKey],
    queryFn: () => fetchClaims(params),
    enabled: !!user,
    refetchOnMount: true,
  });

  return {
    data: data?.items ?? [],
    hasMore: data?.nextCursor != null,
    nextCursor: data?.nextCursor ?? null,
    loading: isLoading,
    error: error?.message ?? null,
    refetch,
  };
}

export function useUpdateClaim() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ claimId, payload }: { claimId: string; payload: UpdateClaimPayload }) =>
      updateClaimService(claimId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  return {
    updateClaim: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error?.message ?? null,
  };
}
